// src/components/common/SidebarFooter.js
//
// The sidebar footer (company avatar + quick-nav icon row) that each page's
// left rail renders at its bottom edge. Dashboard/Data/Settings carry inline
// copies of this markup; this shared component was extracted for the Agent
// sidebar and is the migration target for those copies. Navigation consults
// the canvasEditGuard bridge, so unsent workbook edits on the Agent page
// prompt before leaving.
import React from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Box, IconButton, Tooltip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../contexts/AuthContext';
import { guardAction, useGuardedLinkClick } from '../../utils/canvasEditGuard';
import { docsUrl } from '../../services/docs';

const navIconSx = {
  color: 'text.secondary',
  mr: 1,
  '&:hover': {
    color: 'text.primary',
    backgroundColor: (theme) => (
      theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
    ),
  },
};

function SidebarFooter({ page }) {
  const { companyName, logout } = useAuth();
  const guardNav = useGuardedLinkClick();

  // Page navigation lives in the Header, so the footer carries only Settings
  // (omitted on the Settings page itself), Docs, and Logout.
  const links = page === 'settings' ? [] : [
    { title: 'Settings', to: '/settings', icon: <SettingsIcon fontSize="small" /> },
  ];

  return (
    <Box sx={{ pt: 2, borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: 32,
            height: 32,
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          {companyName ? companyName.charAt(0).toUpperCase() : 'D'}
        </Avatar>
        <Box>
          {links.map((link) => (
            <Tooltip key={link.to} title={link.title} arrow placement="top">
              <IconButton
                size="small"
                component={Link}
                to={link.to}
                onClick={guardNav(link.to)}
                sx={navIconSx}
              >
                {link.icon}
              </IconButton>
            </Tooltip>
          ))}
          <Tooltip title="Documentation" arrow placement="top">
            <IconButton
              size="small"
              component="a"
              href={docsUrl()}
              target="_blank"
              rel="noopener noreferrer"
              sx={navIconSx}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Logout" arrow placement="top">
            <IconButton
              size="small"
              onClick={() => guardAction(() => {
                // A failed sign-out is reported by the auth gate; nothing to add here.
                logout().catch(() => {});
              })}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main',
                  backgroundColor: 'rgba(232, 74, 95, 0.05)',
                },
              }}
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

export default SidebarFooter;
